import { InfiniteData, QueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { toNumber } from 'lodash';

import { InfiniteScrollData } from '@/components/common/infinite-scroll';
import { SearchHospitalService } from '@/services/hospital/SearchHospitalService';
import * as Types from '@/types/bff/gql-schema';
import { GraphqlError } from '@/types/error';
import { InfluenzaHospitalVo, RegionByDepthVo } from '@/types/hospital';

import { SearchInfluenzaHospitalsQuery } from '@/types/bff/gql-schema';
import { getDiagnosisStateOfOperatingStatus } from '@/utils/format';
import { getFormattedReviewTotalCount } from '@/utils/format';
import { getReviewScore } from '@/utils/format';
import { useSearchInfluenzaHospitalsQuery } from './f2fSearch.gql.impl';

const parseInfluenzaHospitalByKeywordVo = (
  data: InfiniteData<SearchInfluenzaHospitalsQuery>
): InfiniteData<InfluenzaHospitalVo[]> => {
  return {
    ...data,
    pages: data.pages.map(
      (page) =>
        page.searchInfluenzaHospitals?.elements.map((hospital) => ({
          ...hospital,
          hospitalRepresentativeDepartmentName: hospital.hospitalRepresentativeDepartmentName ?? '',
          hospitalTotalCount: page.searchInfluenzaHospitals?.totalCount ?? 0,
          reviewScore: getReviewScore(hospital.reviewAverageRating),
          reviewCount: getFormattedReviewTotalCount(hospital.reviewTotalCount),
          hospitalOperationStatus: getDiagnosisStateOfOperatingStatus(hospital.hospitalOperationStatus),
          nonBenefit: {
            diagnosisPrice: Number(hospital.influenzaPriceWithMetadata?.diagnosisPrice) ?? 0,
            labels: hospital.influenzaPriceWithMetadata?.labels ?? [],
          },
          doctors: [],
        })) ?? []
    ),
  };
};

const DEFAULT_SIZE = 30;

interface SearchHospitalsInfinityQuery extends InfiniteScrollData<InfluenzaHospitalVo[]> {
  remove: () => void;
}
const useSearchInfluenzaHospitalsInfinityQuery = (
  request: Types.HospitalInfluenzaSearchRequestInput
): SearchHospitalsInfinityQuery => {
  const limit = request.limit || DEFAULT_SIZE;

  const infinityQuery = useInfiniteQuery<Types.SearchInfluenzaHospitalsQuery, GraphqlError, InfluenzaHospitalVo[]>(
    useSearchInfluenzaHospitalsQuery.getKey({
      request: { ...request, limit },
    }),
    ({
      pageParam,
    }: {
      pageParam?: {
        pivot?: number;
        pivotScore?: number;
      };
    }) => {
      return useSearchInfluenzaHospitalsQuery.fetcher({
        request: {
          ...request,
          limit,
          pivotId: pageParam?.pivot,
          pivotScore: pageParam?.pivotScore,
        },
      })();
    },
    {
      select: parseInfluenzaHospitalByKeywordVo,
      getNextPageParam: (lastPage: Types.SearchInfluenzaHospitalsQuery) => {
        const limit = request.limit || DEFAULT_SIZE;
        const lastPageLength = lastPage.searchInfluenzaHospitals?.elements.length ?? 0;

        if (lastPageLength < limit) {
          return undefined;
        }

        const nextPivotId = Number(lastPage.searchInfluenzaHospitals?.pivot);
        const nextPivotScore = Number(lastPage.searchInfluenzaHospitals?.pivotScore);

        return { pivot: nextPivotId, pivotScore: nextPivotScore };
      },
    }
  );

  const hospitals = infinityQuery.data?.pages.flatMap((hospital) => hospital) ?? [];

  return { ...infinityQuery, data: hospitals };
};

export default useSearchInfluenzaHospitalsInfinityQuery;

useSearchInfluenzaHospitalsInfinityQuery.prefetchQuery = async (queryClient: QueryClient, queries: RegionByDepthVo) => {
  const queryParams: Types.HospitalInfluenzaSearchRequestInput = {
    geoPoint: getCoordinate(),
    limit: DEFAULT_SIZE,
    sortType: Types.HospitalSortType.Distance,
    regionByDepth: getRegionByQuery(queries),
  };

  await queryClient.prefetchInfiniteQuery(
    useSearchInfluenzaHospitalsQuery.getKey({
      request: queryParams,
    }),
    useSearchInfluenzaHospitalsQuery.fetcher({
      request: queryParams,
    })
  );
};

const getCoordinate = (queryLatitude?: string, queryLongitude?: string) => {
  if (!queryLatitude || !queryLongitude) {
    return SearchHospitalService.initializeUserCoordinate();
  }

  const latitude = toNumber(queryLatitude);
  const longitude = toNumber(queryLongitude);

  if (isNaN(latitude) || isNaN(longitude)) {
    return SearchHospitalService.initializeUserCoordinate();
  }

  return { latitude, longitude };
};

const getRegionByQuery = (queries: RegionByDepthVo) => {
  if (!queries?.siDoName) {
    return undefined;
  }

  return {
    legalEupMyeonDongName: queries?.legalEupMyeonDongName,
    siGunGuName: queries?.siGunGuName,
    siDoName: queries?.siDoName ?? '',
  };
};
